<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Support" icon="fa-life-ring"/>

            <div class="content mt-4">
                <p class="has-text-white" v-if="supportLoading && !supportTickets.length && !selectedTicket"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading tickets...</p>

                <!-- Ticket Detail View -->
                <div v-else-if="selectedTicket">
                    <div class="mb-5">
                        <a @click="backToList" class="back-link"><i class="far fa-arrow-left mr-2"></i>Back to Tickets</a>
                    </div>

                    <div class="ticket-panel">
                        <div class="ticket-detail-header mb-5">
                            <div class="columns is-mobile is-vcentered">
                                <div class="column">
                                    <h3 class="ticket-subject">{{ selectedTicket.subject }}</h3>
                                    <div class="ticket-meta mt-1">Opened {{ formatDate(selectedTicket.created_at) }}</div>
                                </div>
                                <div class="column is-narrow">
                                    <b-tag :type="statusType(selectedTicket.status)" size="is-medium">{{ statusLabel(selectedTicket.status) }}</b-tag>
                                </div>
                            </div>
                        </div>

                        <div class="messages-thread">
                            <div v-for="(msg, index) in selectedTicket.support_messages" :key="msg.id"
                                 class="message-item" :class="{'is-user': msg.is_inbound, 'is-support': !msg.is_inbound}">
                                <div class="columns is-gapless">
                                    <div class="column is-narrow mr-4">
                                        <div class="avatar" :class="{'user-avatar': msg.is_inbound, 'support-avatar': !msg.is_inbound}">
                                            <i :class="msg.is_inbound ? 'fas fa-user' : 'fas fa-headset'"></i>
                                        </div>
                                    </div>
                                    <div class="column">
                                        <div class="msg-info">
                                            <strong>{{ msg.is_inbound ? 'You' : formatSender(msg.sender) }}</strong>
                                            <span class="message-date">{{ formatDate(msg.created_at) }}</span>
                                        </div>
                                        <iframe v-if="msg.body_html" class="message-iframe" :srcdoc="msg.body_html" sandbox="allow-same-origin" frameborder="0" @load="resizeIframe($event)"></iframe>
                                        <div class="message-body" v-else>{{ msg.body_text }}</div>
                                    </div>
                                </div>
                                <hr v-if="index < selectedTicket.support_messages.length - 1"/>
                            </div>
                        </div>

                        <div v-if="selectedTicket.status !== 'closed'" class="reply-section">
                            <h4 class="mb-3">Reply</h4>
                            <b-field>
                                <b-input v-model="replyText" type="textarea" placeholder="Type your reply..." rows="3"></b-input>
                            </b-field>
                            <b-button type="is-primary" @click="submitReply" :loading="sending" :disabled="!replyText.trim()">
                                <i class="far fa-paper-plane mr-2"></i>Send Reply
                            </b-button>
                        </div>
                        <div v-else class="closed-notice">
                            <i class="far fa-info-circle mr-2"></i>This ticket is closed. If you need further help, please open a new ticket.
                        </div>
                    </div>
                </div>

                <!-- New Ticket Form -->
                <div v-else-if="showNewForm">
                    <div class="mb-5">
                        <a @click="showNewForm = false" class="back-link"><i class="far fa-arrow-left mr-2"></i>Back to Tickets</a>
                    </div>

                    <h1 class="has-text-white mt-2 mb-2">New Support Ticket</h1>

                    <div class="ticket-panel">
                        <h4 class="mb-4">How can we help?</h4>

                        <b-field label="Subject">
                            <b-input v-model="newTicket.subject" placeholder="Brief description of your issue"></b-input>
                        </b-field>

                        <b-field label="Message" class="mt-4">
                            <b-input v-model="newTicket.body_text" type="textarea" placeholder="Describe your issue in detail..." rows="6"></b-input>
                        </b-field>

                        <hr/>

                        <b-button type="is-primary" size="is-medium" @click="submitNewTicket" :loading="sending"
                                  :disabled="!newTicket.subject.trim() || !newTicket.body_text.trim()">
                            <i class="far fa-paper-plane mr-2"></i>Submit Ticket
                        </b-button>
                    </div>
                </div>

                <!-- Ticket List View -->
                <div v-else>
                    <div class="columns pt-6">
                        <div class="column">
                            <div class="columns is-mobile is-vcentered mb-2">
                                <div class="column">
                                    <h2 class="list-heading mb-0"><i class="fad fa-life-ring fa-fw mr-3"></i>Your Tickets</h2>
                                </div>
                                <div class="column is-narrow">
                                    <b-button type="is-primary" @click="showNewForm = true"><i class="far fa-plus mr-2"></i>New Ticket</b-button>
                                </div>
                            </div>

                            <div v-if="!supportLoading && supportTickets.length === 0" class="empty-state mt-5">
                                <div class="columns">
                                    <div class="column is-narrow is-hidden-touch">
                                        <i class="fad fa-ticket fa-3x" style="color: #9faec2;"></i>
                                    </div>
                                    <div class="column">
                                        <p class="has-text-weight-bold">No support tickets yet</p>
                                        <p class="meta">Have a question or need help? Open a new ticket and our team will get back to you.</p>
                                    </div>
                                </div>
                            </div>

                            <div v-for="ticket in supportTickets" :key="ticket.id"
                                 class="ticket-card mb-4" @click="openTicket(ticket.id)">
                                <div class="information">
                                    <div class="columns is-mobile">
                                        <div class="column is-narrow is-hidden-touch">
                                            <i class="fad fa-ticket has-text-primary fa-2x"></i>
                                        </div>
                                        <div class="column">
                                            <div class="columns is-block-tablet is-flex-widescreen is-vcentered">
                                                <div class="column">
                                                    <div class="has-text-weight-bold">
                                                        <i class="fad fa-ticket has-text-primary is-hidden-desktop mr-2"></i>
                                                        {{ ticket.subject }}
                                                    </div>
                                                    <div class="meta">{{ formatDate(ticket.last_message_at || ticket.created_at) }}</div>
                                                    <div class="meta" v-if="ticket._count">{{ ticket._count.support_messages }} message{{ ticket._count.support_messages !== 1 ? 's' : '' }}</div>
                                                </div>
                                                <div class="column is-narrow has-text-right">
                                                    <b-tag :type="statusType(ticket.status)">{{ statusLabel(ticket.status) }}</b-tag>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import {mapGetters, mapActions} from "vuex";
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";

    export default {
        components: {Framework, SidePanelNav, MainHeader},
        data() {
            return {
                selectedTicket: null,
                showNewForm: false,
                sending: false,
                replyText: '',
                newTicket: {
                    subject: '',
                    body_text: ''
                }
            };
        },
        computed: {
            ...mapGetters(['supportTickets', 'currentTicket', 'supportLoading']),
        },
        methods: {
            ...mapActions(['loadTickets', 'loadTicket', 'createTicket', 'sendReply']),
            openTicket(id) {
                this.loadTicket(id).then(() => {
                    this.selectedTicket = this.currentTicket;
                    this.replyText = '';
                });
            },
            backToList() {
                this.selectedTicket = null;
                this.loadTickets();
            },
            submitNewTicket() {
                this.sending = true;
                this.createTicket({
                    subject: this.newTicket.subject,
                    body_text: this.newTicket.body_text
                }).then((result) => {
                    this.showNewForm = false;
                    this.newTicket = {subject: '', body_text: ''};
                    this.openTicket(result.id);
                }).finally(() => {
                    this.sending = false;
                });
            },
            submitReply() {
                if (!this.replyText.trim()) return;
                this.sending = true;
                this.sendReply({
                    id: this.selectedTicket.id,
                    data: {body_text: this.replyText}
                }).then(() => {
                    this.replyText = '';
                    this.selectedTicket = this.currentTicket;
                }).finally(() => {
                    this.sending = false;
                });
            },
            statusType(status) {
                switch (status) {
                    case 'open': return 'is-success';
                    case 'assigned': return 'is-info';
                    case 'on_hold': return 'is-warning';
                    case 'closed': return 'is-light';
                    default: return 'is-light';
                }
            },
            statusLabel(status) {
                switch (status) {
                    case 'open': return 'Open';
                    case 'assigned': return 'Assigned';
                    case 'on_hold': return 'On Hold';
                    case 'closed': return 'Closed';
                    default: return status;
                }
            },
            formatDate(dateStr) {
                if (!dateStr) return '';
                let d = new Date(dateStr);
                return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'});
            },
            formatSender(sender) {
                if (!sender) return 'Support Team';
                // Strip email portion like "Name <email@example.com>" → "Name"
                let name = sender.replace(/<[^>]+>/, '').trim();
                // If name is empty or "undefined undefined", fall back
                if (!name || name === 'undefined undefined' || name === 'undefined') return 'Support Team';
                return name;
            },
            resizeIframe(e) {
                let iframe = e.target;
                try {
                    let doc = iframe.contentDocument || iframe.contentWindow.document;
                    let style = doc.createElement('style');
                    style.textContent = 'html, body { margin: 0; padding: 0; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; font-size: 0.85rem; line-height: 1.5; color: #333; word-break: break-word; } img { max-width: 100%; height: auto; }';
                    doc.head.appendChild(style);
                    setTimeout(() => {
                        iframe.style.height = doc.body.scrollHeight + 'px';
                    }, 0);
                } catch (err) {}
            }
        },
        mounted() {
            this.loadTickets();
        }
    };
</script>

<style lang="scss" scoped>
    .list-heading {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    .back-link {
        color: #9faec2;
        font-size: 0.9rem;

        &:hover {
            color: white;
        }
    }

    .ticket-card {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        cursor: pointer;
        transition: transform 150ms;

        &:hover {
            transform: translateY(-1px);
        }

        .information {
            padding: 1.5rem;
        }

        .meta {
            font-weight: normal;
            font-size: 0.8rem;
            color: #737881;
        }
    }

    .empty-state {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 2.5rem;

        p {
            color: #4a4a4a;
        }

        .meta {
            font-size: 0.9rem;
            color: #737881;
            margin-top: 0.25rem;
        }
    }

    .ticket-panel {
        background-color: white;
        border-radius: 4px;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
        padding: 3rem 3rem 4rem;
        margin-top: 2rem;
        margin-bottom: 6rem;

        @media (max-width: 1180px) {
            padding: 2rem;
        }

        @media (max-width: 768px) {
            padding: 1rem;
        }
    }

    .ticket-detail-header {
        border-bottom: 2px solid #e0e2e8;
        padding-bottom: 1.25rem;

        .ticket-subject {
            font-size: 1.3rem;
            font-weight: bold;
            color: #224C74;
            margin: 0;
        }

        .ticket-meta {
            font-size: 0.8rem;
            color: #737881;
        }
    }

    .messages-thread {
        .message-item {
            padding: 0.5rem 0;

            hr {
                margin: 1.5rem 0;
                background-color: #e0e2e8;
                height: 1px;
            }

            .avatar {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 0.85rem;
                color: white;
                margin-top: 2px;

                &.user-avatar {
                    background-color: #336699;
                }

                &.support-avatar {
                    background-color: #48c774;
                }
            }

            .msg-info {
                strong {
                    color: #363636;
                }

                .message-date {
                    color: #999;
                    font-size: 0.8rem;
                    margin-left: 0.75rem;
                }
            }

            .message-iframe {
                width: 100%;
                min-height: 40px;
                border: none;
                overflow: hidden;
                margin-top: 0.35rem;
            }

            .message-body {
                color: #4a4a4a;
                line-height: 1.6;
                margin-top: 0.35rem;
                white-space: pre-wrap;
            }
        }
    }

    .reply-section {
        border-top: 2px solid #e0e2e8;
        margin-top: 2rem;
        padding-top: 1.5rem;

        h4 {
            color: #224C74;
            font-weight: bold;
        }
    }

    .closed-notice {
        border-top: 2px solid #e0e2e8;
        margin-top: 2rem;
        padding-top: 1.5rem;
        color: #737881;
        font-size: 0.95rem;
    }
</style>
